
import Vue from 'vue';
import Component from 'vue-class-component';
import {Watch} from 'vue-property-decorator';
import {Getter, Action} from 'vuex-class';
import {AppState, UyapGirisAsamasi, UyapStatusMessage} from '@/types';
import {EicraproDurumu, eicraproBekleniyorHatasiMi} from '@/utils/eicraproBaglantisi';
import {
  BOT_BEKLEME_METINLERI,
  BotBeklemeSebebi,
  botBeklemeGorunurMu,
  elleBaglanmaHatasiGosterilsinMi,
  kodtanSebebe
} from '@/utils/botBekleme';
import {PlayerDurum} from '@/plugins/uyap-plugin/store/modules/GorevYoneticisiTypes';

interface PrimaryActionConfig {
  label: string;
  icon: string;
  color: string;
  outlined: boolean;
}

interface GirisAdimi {
  key: UyapGirisAsamasi;
  label: string;
  icon: string;
  aktif: boolean;
  bitti: boolean;
}

// Giriş adımlarının sırası. statusDescription ve adım listesi aynı kaynaktan
// beslensin diye tek yerde duruyor.
const GIRIS_ADIMLARI: Array<{ key: UyapGirisAsamasi; label: string }> = [
  {key: 'oturum-kontrol', label: 'Mevcut oturum kontrol ediliyor'},
  {key: 'imza', label: 'E-imza ile giriş yapılıyor'},
  {key: 'bilgiler', label: 'Avukat bilgileri alınıyor'}
];

@Component({})
export default class UyapStatusCard extends Vue {
  @Getter isUyapConnected!: boolean;
  @Getter isUyapConnecting!: boolean;
  @Getter uyapGirisAsamasi!: UyapGirisAsamasi | null;
  @Getter uyapStatusMessage!: UyapStatusMessage | null;
  @Getter healthCheckRetryCount!: number;
  @Getter isHealthChecking!: boolean;
  @Getter availableCardList!: any[];
  @Getter eicraproDurumu!: EicraproDurumu;
  @Getter eicraproDurumMesaji!: string;
  @Getter botBeklemeSebebi!: BotBeklemeSebebi | null;
  @Getter getPlayerDurum!: PlayerDurum;
  @Action connectToUyap!: () => Promise<void>;
  @Action checkUyapSession!: () => Promise<void>;

  isRefreshing = false;
  // Kullanıcının Bağlan'a son bastığı an (ms; 0 = hiç). Bekleme metni yerine elle bağlanma hatasını
  // göstermek için (statusDescription); bekleme nedeni değişince ya da kalkınca sıfırlanır.
  sonElleDeneme = 0;
  private readonly defaultHealthRetryLimit = 3;

  get hasAvailableCard(): boolean {
    return Array.isArray(this.availableCardList) && this.availableCardList.length > 0;
  }

  // UYAP girişi eİcraPro kimliği gelene kadar bekler (store connectToUyap kapısı).
  // Bekleme bir hata değil; kart kırmızısı yerine bekleme rengi ve açıklaması gösterilir.
  get eicraproBekleniyor(): boolean {
    return !this.isUyapConnected && !this.isUyapConnecting && this.eicraproDurumu === 'yok';
  }

  // Lisans doğrulaması kimliği reddetti (lisans sahibinin TCKN'si yok / büroda etkin eİcraPro
  // lisansı yok): UYAP girişi ve bot yok, kullanıcı destekle iletişime geçmeli. Bu bir bekleme değil,
  // düzeltilmesi gereken bir durum: hata rengi.
  get tcknEksik(): boolean {
    return !this.isUyapConnected && !this.isUyapConnecting && this.eicraproDurumu === 'tckn-eksik';
  }

  // Kimlik tamam, UYAP bağlı değil ve bot kendi başına ilerleyemiyor (PIN yok, kart yok,
  // program kapalı...): bekleme nedeni durum kartında gösterilir, bot engel kalkınca kendiliğinden devam eder.
  // Yalnız bot ÇALIŞIRKEN ('oto'): Durdur'a basılmış bota "kendiliğinden devam eder" denmez.
  get botBekliyor(): boolean {
    return !this.isUyapConnected && !this.isUyapConnecting && this.eicraproDurumu === 'tamam' &&
        botBeklemeGorunurMu(this.botBeklemeSebebi, this.getPlayerDurum);
  }

  @Watch('botBeklemeSebebi')
  onBotBeklemeSebebiDegisti(): void {
    this.sonElleDeneme = 0;
  }

  // Kart yokken ya da eİcraPro kimliği (lisans doğrulamalı) tamamlanmadan "Bağlan" tıklanamaz;
  // bağlıyken buton "Yenile" olduğu için etkilenmez.
  get isPrimaryDisabled(): boolean {
    return !this.isUyapConnected && (!this.hasAvailableCard || this.eicraproDurumu !== 'tamam');
  }

  get girisAdimlari(): GirisAdimi[] {
    const aktifIndex = GIRIS_ADIMLARI.findIndex(adim => adim.key === this.uyapGirisAsamasi);
    return GIRIS_ADIMLARI.map((adim, index) => {
      const bitti = aktifIndex >= 0 && index < aktifIndex;
      const aktif = index === aktifIndex;
      return {
        key: adim.key,
        label: adim.label,
        aktif,
        bitti,
        icon: bitti ? 'mdi-check-circle' : (aktif ? 'mdi-circle-slice-5' : 'mdi-circle-outline')
      };
    });
  }

  get statusLabel(): string {
    if (this.isUyapConnecting) return 'Bağlanıyor';
    if (this.tcknEksik) return 'TC Kimlik Eksik';
    if (this.eicraproBekleniyor) return 'eİcraPro Bekleniyor';
    if (this.botBekliyor) return 'Bot Bekliyor';
    const type = this.uyapStatusMessage?.type;
    if (type === 'info') return 'Kontrol Ediliyor';
    return this.isUyapConnected ? 'Bağlı' : 'Bağlı Değil';
  }

  get statusIcon(): string {
    if (this.tcknEksik) return 'mdi-alert-circle';
    if (this.isUyapConnecting || this.eicraproBekleniyor || this.botBekliyor) return 'mdi-progress-clock';
    const type = this.uyapStatusMessage?.type;
    if (type === 'info') return 'mdi-progress-clock';
    return this.isUyapConnected ? 'mdi-check-circle' : 'mdi-alert-circle';
  }

  get statusColor(): string {
    // eicrapro turuncusu (App.vue .eup-bg-orange ile aynı). Geçiş durumu hem
    // "bağlı"nın yeşilinden hem "değil"in kırmızısından ayrı, hem de markanın rengi.
    if (this.isUyapConnecting) return '#E76100';
    if (this.tcknEksik) return '#ef5350';
    if (this.eicraproBekleniyor || this.botBekliyor) return '#E1893F';
    const type = this.uyapStatusMessage?.type;
    if (type === 'error') return '#ef5350';
    if (type === 'success' || this.isUyapConnected) return 'success darken-2';
    if (type === 'info') return '#E1893F';
    return 'error darken-2';
  }

  get statusIconColor(): string {
    if (this.isUyapConnecting) return '#ffffff';
    if (this.tcknEksik) return '#ff5252';
    if (this.eicraproBekleniyor || this.botBekliyor) return '#f59e0b';
    const type = this.uyapStatusMessage?.type;
    if (type === 'success' || this.isUyapConnected) return '#4caf50';
    if (type === 'error') return '#ff5252';
    if (type === 'info') return '#f59e0b';
    return '#facc15';
  }

  get statusIconBubbleColor(): string {
    if (this.isUyapConnecting) return 'rgba(255, 255, 255, 0.2)';
    if (this.tcknEksik) return 'rgba(244, 67, 54, 0.15)';
    if (this.eicraproBekleniyor || this.botBekliyor) return 'rgba(245, 158, 11, 0.15)';
    const type = this.uyapStatusMessage?.type;
    if (type === 'success' || this.isUyapConnected) return 'rgba(76, 175, 80, 0.15)';
    if (type === 'error') return 'rgba(244, 67, 54, 0.15)';
    if (type === 'info') return 'rgba(245, 158, 11, 0.15)';
    return 'rgba(250, 204, 21, 0.15)';
  }

  get statusTextColor(): string {
    if (this.isUyapConnecting) return 'white';
    if (this.tcknEksik) return 'white';
    if (this.eicraproBekleniyor || this.botBekliyor) return 'black';
    const type = this.uyapStatusMessage?.type;
    if (type === 'info') return 'black';
    return 'white';
  }

  get statusDescription(): string {
    // Bağlanırken bayat mesaj gösterilmez: önceki denemenin hata metni,
    // sürmekte olan girişin üstünde "başarısız" izlenimi bırakıyordu.
    if (this.isUyapConnecting) {
      return 'UYAP oturumu açılıyor, lütfen bekleyin.';
    }
    // Kimlik durumu (TCKN eksik / lisans yok / eİcraPro bekleniyor) önceki bir denemenin hata
    // mesajından önce gelir: giriş zaten kimlik tamamlanana kadar denenmiyor.
    if (this.tcknEksik || this.eicraproBekleniyor) {
      return this.eicraproDurumMesaji;
    }
    // Bot bekleme nedeni (PIN yok, kart yok...) durum mesajının önüne geçer: kullanıcıya yapılacak
    // adımı söyler. İstisna: kullanıcı Bağlan'a basıp başarısız olduysa (ör. giriş sonrası meta veri ya
    // da kimlik denetimi hatası) gerçek neden o hatadır; bayat bekleme metni onu gizlemez.
    if (this.botBekliyor && this.botBeklemeSebebi &&
        !elleBaglanmaHatasiGosterilsinMi(this.uyapStatusMessage, this.sonElleDeneme)) {
      return BOT_BEKLEME_METINLERI[this.botBeklemeSebebi].durum;
    }
    const message = this.uyapStatusMessage?.text;
    if (!message) {
      if (this.isUyapConnected) {
        return 'UYAP bağlantısı aktif ve hazır.';
      }
      if (!this.hasAvailableCard) {
        return 'E-imza kartı takılı değil. Kartı taktığınızda bağlantı kendiliğinden kurulur.';
      }
      return 'Giriş yapılmadı veya oturum süresi doldu.';
    }
    return this.normalizeMessage(message);
  }

  private normalizeMessage(raw: string): string {
    let value = raw.trim();
    value = value.replace(/^error:\s*/i, '');
    if (!value) return 'UYAP bağlantısı başarısız.';

    const lower = value.toLowerCase();
    // PIN mesajları (yanlış/kilitli PIN) "kart" kelimesi geçse de kart yokluğu değildir: olduğu gibi gösterilir.
    if (/p[iı]n/.test(lower)) {
      return value;
    }
    if (lower.includes('kart') || lower.includes('takılı') || lower.includes('takili')) {
      return 'İmzacı takılı değil.';
    }
    if (lower.includes('timeout') || lower.includes('zaman')) {
      return 'UYAP cevap vermedi, lütfen tekrar deneyin.';
    }
    if (lower.includes('uyap bağlantısı yapılamadı')) {
      return 'UYAP bağlantısı yapılamadı. Lütfen tekrar deneyin.';
    }
    if (lower.includes('bekleyen') && lower.includes('giriş')) {
      return "UYAP'ta bekleyen bir giriş isteği bulunuyor. Tarayıcıdan isteği onaylayın veya iptal edin.";
    }
    return value;
  }

  get maxHealthCheckRetries(): number {
    const limit = (this.$store.state as AppState).healthCheckMaxRetries;
    return typeof limit === 'number' ? limit : this.defaultHealthRetryLimit;
  }

  get heartbeatIcon(): string {
    return this.isHealthChecking ? 'mdi-pulse' : 'mdi-heart-off';
  }

  get heartbeatIconColor(): string {
    // Turuncu zeminde yeşil/amber ikon kayboluyor; bağlanırken beyaz kalıyor.
    if (this.isUyapConnecting) return 'white';
    return this.isHealthChecking ? 'green darken-1' : 'amber darken-2';
  }

  get heartbeatLabel(): string {
    return this.isHealthChecking ? 'Otomatik izleme aktif' : 'Otomatik izleme kapalı';
  }

  get primaryAction(): PrimaryActionConfig {
    if (this.isUyapConnecting) {
      return {
        label: 'Bağlanıyor',
        icon: 'mdi-progress-clock',
        // Turuncu zeminde beyaz outline düğme okunur kalıyor.
        color: 'white',
        outlined: true,
      };
    }
    if (this.isUyapConnected) {
      return {
        label: 'Yenile',
        icon: 'mdi-refresh',
        color: 'grey darken-3',
        outlined: true,
      };
    }

    return {
      label: 'Bağlan',
      icon: 'mdi-link-variant',
      color: 'success darken-1',
      outlined: false,
    };
  }

  // Time: O(1), Space: O(1) — tek aksiyon tetikleyicisi.
  async handlePrimaryAction() {
    if (this.isRefreshing) return;
    this.isRefreshing = true;
    this.sonElleDeneme = Date.now();
    try {
      if (this.isUyapConnected) {
        await this.checkUyapSession();
      } else {
        await this.connectToUyap();
      }
    } catch (error) {
      if (eicraproBekleniyorHatasiMi(error)) {
        this.$toast?.info?.((error as Error).message);
        return;
      }
      // İnsan gerektiren KODLU hata (iptal/yanlış PIN: PIN_GEREKLI, KART_YOK...): bekleme metni yapılacak
      // adımı ("Bağlan'a basıp PIN'inizi girin") söylüyor, genel hata onun yerine geçmez. Kodsuz hata
      // (giriş sonrası meta veri / kimlik denetimi) gerçek nedendir ve bekleme metninin önüne geçer.
      if (kodtanSebebe((error as {kod?: unknown} | null)?.kod) !== null) {
        this.sonElleDeneme = 0;
        // Nedeni plugin'in açtığı diyalog söyledi (PIN yanlış/kilitli, kart yok...); "tekrar deneyin"
        // toast'ı kilitli kartta yanlış yönlendirir.
        console.error('UYAP bağlantı işlemi başarısız.', error);
        return;
      }
      console.error('UYAP bağlantı işlemi başarısız.', error);
      this.$toast?.error?.('UYAP bağlantısı sağlanamadı. Lütfen tekrar deneyin.');
    } finally {
      this.isRefreshing = false;
    }
  }
}
